import { paths } from "src/paths";
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Chip, SvgIcon } from '@mui/material';
import AlignLeft02Icon from 'src/icons/untitled-ui/duocolor/align-left-02';
import BarChartSquare02Icon from 'src/icons/untitled-ui/duocolor/bar-chart-square-02';
import Building04Icon from 'src/icons/untitled-ui/duocolor/building-04';
import CalendarIcon from 'src/icons/untitled-ui/duocolor/calendar';
import CheckDone01Icon from 'src/icons/untitled-ui/duocolor/check-done-01';
import CreditCard01Icon from 'src/icons/untitled-ui/duocolor/credit-card-01';
import CurrencyBitcoinCircleIcon from 'src/icons/untitled-ui/duocolor/currency-bitcoin-circle';
import File01Icon from 'src/icons/untitled-ui/duocolor/file-01';
import GraduationHat01Icon from 'src/icons/untitled-ui/duocolor/graduation-hat-01';
import HomeSmileIcon from 'src/icons/untitled-ui/duocolor/home-smile';
import LayoutAlt02Icon from 'src/icons/untitled-ui/duocolor/layout-alt-02';
import LineChartUp04Icon from 'src/icons/untitled-ui/duocolor/line-chart-up-04';
import Lock01Icon from 'src/icons/untitled-ui/duocolor/lock-01';
import LogOut01Icon from 'src/icons/untitled-ui/duocolor/log-out-01';
import Mail03Icon from 'src/icons/untitled-ui/duocolor/mail-03';
import Mail04Icon from 'src/icons/untitled-ui/duocolor/mail-04';
import MessageChatSquareIcon from 'src/icons/untitled-ui/duocolor/message-chat-square';
import ReceiptCheckIcon from 'src/icons/untitled-ui/duocolor/receipt-check';
import Share07Icon from 'src/icons/untitled-ui/duocolor/share-07';
import ShoppingBag03Icon from 'src/icons/untitled-ui/duocolor/shopping-bag-03';
import ShoppingCart01Icon from 'src/icons/untitled-ui/duocolor/shopping-cart-01';
import Truck01Icon from 'src/icons/untitled-ui/duocolor/truck-01';
import Upload04Icon from 'src/icons/untitled-ui/duocolor/upload-04';
import Users03Icon from 'src/icons/untitled-ui/duocolor/users-03';
import XSquareIcon from 'src/icons/untitled-ui/duocolor/x-square';
import { tokens } from 'src/locales/tokens';
import { roles } from 'src/roles';
import { useAuth } from "../../hooks/use-auth";
import EngineeringIcon from '@mui/icons-material/Engineering';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import PrecisionManufacturingOutlinedIcon from '@mui/icons-material/PrecisionManufacturingOutlined';
import GridViewIcon from '@mui/icons-material/GridView';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import CardMembershipOutlinedIcon from '@mui/icons-material/CardMembershipOutlined';
import MonetizationOnOutlinedIcon from '@mui/icons-material/MonetizationOnOutlined';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import { tr } from 'src/locales/tr';

const isRoleAllowed = (userRole, itemRole) => {
    if (!itemRole) {
        return true;
    }

    if (!userRole) {
        return false;
    }

    if (userRole === roles.ADMIN) {
        return true;
    }

    if (Array.isArray(itemRole)) {
        return itemRole.includes(userRole);
    }

    return itemRole === userRole;
};

const filterNavItems = (items, userRole) => {
    return items
        .map((item) => {
            if (item.items) {
                const filteredChildren = filterNavItems(item.items, userRole);
                const allowedSelf = isRoleAllowed(userRole, item.role);

                if (!allowedSelf && filteredChildren.length === 0) {
                    return null;
                }

                return {
                    ...item,
                    items: filteredChildren
                };
            }

            return isRoleAllowed(userRole, item.role) ? item : null;
        })
        .filter(Boolean);
};

export const useSections = () => {
    const { t } = useTranslation();
    const auth = useAuth();
    const user = auth.user;
    const userRole = user?.role;

    return useMemo(() => {
        if (userRole !== roles.ADMIN) {
            const dashboardItems = [
                {
                    title: tr("Overview"),
                    path: paths.dashboard.overview,
                    icon: (
                        <SvgIcon fontSize="small">
                            <GridViewIcon />
                        </SvgIcon>
                    )
                },
                {
                    title: tr("Profile settings"),
                    path: paths.dashboard.profile.information,
                    icon: (
                        <SvgIcon fontSize="small">
                            <AccountCircleOutlinedIcon />
                        </SvgIcon>
                    ),
                    items: [
                        {
                            title: tr("Information"),
                            path: paths.dashboard.profile.information,
                            icon: (
                                <SvgIcon fontSize="small">
                                    <InfoOutlinedIcon />
                                </SvgIcon>
                            )
                        },
                        {
                            title: tr("Security & access"),
                            path: paths.dashboard.profile.securityAccess,
                            icon: (
                                <SvgIcon fontSize="small">
                                    <NotificationsNoneOutlinedIcon />
                                </SvgIcon>
                            )
                        },
                        {
                            title: tr("Notifications"),
                            path: paths.dashboard.profile.notifications,
                            icon: (
                                <SvgIcon fontSize="small">
                                    <NotificationsNoneOutlinedIcon />
                                </SvgIcon>
                            )
                        }
                    ]
                },
                {
                    title: tr("Trades"),
                    path: paths.dashboard.trades.index,
                    role: roles.WORKER,
                    icon: (
                        <SvgIcon fontSize="small">
                            <PrecisionManufacturingOutlinedIcon />
                        </SvgIcon>
                    )
                },
                {
                    title: tr("Certificates"),
                    path: paths.dashboard.certificates.index,
                    role: roles.WORKER,
                    icon: (
                        <SvgIcon fontSize="small">
                            <CardMembershipOutlinedIcon />
                        </SvgIcon>
                    )
                },
                {
                    title: tr("My Requests"),
                    path: paths.dashboard.requests.index,
                    role: roles.CUSTOMER,
                    icon: (
                        <SvgIcon fontSize="small">
                            <AssignmentOutlinedIcon />
                        </SvgIcon>
                    )
                }
            ];

            return [
                {
                    subheader: tr("Dashboard"),
                    items: filterNavItems(dashboardItems, userRole)
                },
                {
                    subheader: tr("Services"),
                    items: [{
                        title: t(tokens.nav.blog),
                        path: paths.dashboard.blog.index,
                        icon: (
                            <SvgIcon fontSize="small">
                                <LayoutAlt02Icon />
                            </SvgIcon>
                        ),
                        items: [
                            {
                                title: tr("All posts"),
                                path: paths.dashboard.blog.index
                            },
                            {
                                title: tr("My posts"),
                                path: paths.dashboard.blog.myPosts
                            }
                        ]
                    },
                    {
                        title: tr("Listings"),
                        path: paths.dashboard.listings.index,
                        icon: (
                            <SvgIcon fontSize="small">
                                <LayoutAlt02Icon />
                            </SvgIcon>
                        ),
                        items: [
                            {
                                title: tr("My listings"),
                                path: paths.dashboard.listings.index
                            },
                            {
                                title: tr("History view"),
                                path: paths.dashboard.listings.history
                            }
                        ]
                    }]
                }
            ];
        }

        let sections = [
            {
                subheader: tr("Site Pages"),
                items: [
                    {
                        title: tr("Home Page"),
                        path: paths.index,
                        icon: (
                            <SvgIcon fontSize="small">
                                <HomeSmileIcon />
                            </SvgIcon>
                        )
                    },
                    {
                        title: tr("Services"),
                        path: paths.services.index,
                        icon: (
                            <SvgIcon fontSize="small">
                                <Building04Icon />
                            </SvgIcon>
                        )
                    },
                    {
                        title: tr("Mission CTMASS"),
                        path: paths.ourMission,
                        icon: (
                            <SvgIcon fontSize="small">
                                <TrendingUpIcon />
                            </SvgIcon>
                        )
                    },
                    {
                        title: tr("Contacts"),
                        path: paths.contact,
                        icon: (
                            <SvgIcon fontSize="small">
                                <Mail04Icon />
                            </SvgIcon>
                        )
                    },
                ]
            },
            {
                subheader: tr("Dashboard"),
                items: [
                    {
                        title: tr("Overview"),
                        path: paths.dashboard.overview,
                        icon: (
                            <SvgIcon fontSize="small">
                                <GridViewIcon />
                            </SvgIcon>
                        )
                    },
                    {
                        title: tr("Profile settings"),
                        path: paths.dashboard.profile.information,
                        icon: (
                            <SvgIcon fontSize="small">
                                <AccountCircleOutlinedIcon />
                            </SvgIcon>
                        ),
                        items: [
                            {
                                title: tr("Information"),
                                path: paths.dashboard.profile.information,
                                icon: (
                                    <SvgIcon fontSize="small">
                                        <InfoOutlinedIcon />
                                    </SvgIcon>
                                )
                            },
                            {
                                title: tr("Security & access"),
                                path: paths.dashboard.profile.securityAccess,
                                icon: (
                                    <SvgIcon fontSize="small">
                                        <NotificationsNoneOutlinedIcon />
                                    </SvgIcon>
                                )
                            },
                            {
                                title: tr("Notifications"),
                                path: paths.dashboard.profile.notifications,
                                icon: (
                                    <SvgIcon fontSize="small">
                                        <NotificationsNoneOutlinedIcon />
                                    </SvgIcon>
                                )
                            }
                        ]
                    },
                    {
                        title: tr("Trades"),
                        path: paths.dashboard.trades.index,
                        icon: (
                            <SvgIcon fontSize="small">
                                <PrecisionManufacturingOutlinedIcon />
                            </SvgIcon>
                        )
                    },
                    {
                        title: t(tokens.nav.overview),
                        path: paths.dashboard.index,
                        icon: (
                            <SvgIcon fontSize="small">
                                <HomeSmileIcon />
                            </SvgIcon>
                        ),
                        role: [roles.ADMIN]
                    },
                    {
                        title: t(tokens.nav.analytics),
                        path: paths.dashboard.analytics,
                        role: roles.ADMIN,
                        icon: (
                            <SvgIcon fontSize="small">
                                <BarChartSquare02Icon />
                            </SvgIcon>
                        )
                    },
                    {
                        title: t(tokens.nav.ecommerce),
                        path: paths.dashboard.ecommerce,
                        role: roles.ADMIN,
                        icon: (
                            <SvgIcon fontSize="small">
                                <LineChartUp04Icon />
                            </SvgIcon>
                        ),
                    },
                    {
                        title: t(tokens.nav.crypto),
                        path: paths.dashboard.crypto,
                        role: roles.ADMIN,
                        icon: (
                            <SvgIcon fontSize="small">
                                <CurrencyBitcoinCircleIcon />
                            </SvgIcon>
                        ),
                        label: (
                            <Chip
                                color="primary"
                                label={tr("New")}
                                size="small"
                            />
                        )
                    },
                    {
                        title: tr("Profile"),
                        path: paths.dashboard.specialistProfile.index,
                        icon: (
                            <SvgIcon fontSize="small">
                                <HomeSmileIcon />
                            </SvgIcon>
                        )
                    },
                    {
                        title: t(tokens.nav.specialistProfileCreate),
                        path: paths.dashboard.specialistProfile.create,
                        role: roles.ADMIN,
                        icon: (
                            <SvgIcon fontSize="small">
                                <HomeSmileIcon />
                            </SvgIcon>
                        )
                    },
                    {
                        title: t(tokens.nav.servicesFeed),
                        path: paths.dashboard.servicesFeed,
                        role: [roles.TEST],
                        icon: (
                            <SvgIcon fontSize="small">
                                <HomeSmileIcon />
                            </SvgIcon>
                        )
                    },
                    {
                        title: tr("Projects"),
                        path: paths.dashboard.project.index,
                        role: [roles.WORKER, roles.CUSTOMER],
                        icon: (
                            <SvgIcon fontSize="small">
                                <Building04Icon />
                            </SvgIcon>
                        ),
                        items: [
                            {
                                title: tr("My projects"),
                                path: paths.dashboard.project.index
                            },
                            {
                                title: tr("Search"),
                                path: paths.dashboard.project.search
                            },
                            {
                                title: tr("Create"),
                                path: paths.dashboard.project.create
                            }
                        ]
                    }
                ]
            },
            {
                subheader: tr("GENERAL"),
                items: [
                    {
                        title: t(tokens.nav.userSettings),
                        path: paths.dashboard.userSettings,
                        role: [roles.CUSTOMER, roles.WORKER, roles.CONTENT],
                        icon: (
                            <SvgIcon fontSize="small">
                                <EngineeringIcon />
                            </SvgIcon>
                        )
                    },
                ]
            },
            {
                subheader: tr("ADMIN"),
                items: [
                    {
                        title: tr("Admin Panel"),
                        path: paths.dashboard.admin.index,
                        role: roles.ADMIN,
                        icon: (
                            <SvgIcon fontSize="small">
                                <AdminPanelSettingsOutlinedIcon />
                            </SvgIcon>
                        ),
                        items: [
                            {
                                title: tr("Loyalty Rules"),
                                path: paths.dashboard.admin.loyalty,
                                icon: (
                                    <SvgIcon fontSize="small">
                                        <MonetizationOnOutlinedIcon />
                                    </SvgIcon>
                                )
                            },
                            {
                                title: tr("User Balances"),
                                path: paths.dashboard.admin.loyaltyUsers,
                                icon: (
                                    <SvgIcon fontSize="small">
                                        <Users03Icon />
                                    </SvgIcon>
                                )
                            },
                            {
                                title: tr("Shop"),
                                path: paths.dashboard.admin.shop,
                                icon: (
                                    <SvgIcon fontSize="small">
                                        <StorefrontOutlinedIcon />
                                    </SvgIcon>
                                )
                            }
                        ]
                    }
                ]
            },
            {
                subheader: tr("CONTENT"),
                items: [
                    {
                        title: tr("Specialties"),
                        path: paths.dashboard.specialties,
                        icon: (
                            <SvgIcon fontSize="small">
                                <HomeSmileIcon />
                            </SvgIcon>
                        ),
                        role: [roles.ADMIN, roles.CONTENT]
                    },
                    {
                        title: t(tokens.nav.customers),
                        path: paths.dashboard.customers.index,
                        role: [roles.ADMIN],
                        icon: (
                            <SvgIcon fontSize="small">
                                <Users03Icon />
                            </SvgIcon>
                        )
                    },
                    {
                        title: tr("All projects"),
                        path: paths.dashboard.admin.projects,
                        role: [roles.ADMIN],
                        icon: (
                            <SvgIcon fontSize="small">
                                <File01Icon />
                            </SvgIcon>
                        )
                    },
                    {
                        title: tr("All trades"),
                        path: paths.dashboard.admin.trades,
                        role: [roles.ADMIN],
                        icon: (
                            <SvgIcon fontSize="small">
                                <Building04Icon />
                            </SvgIcon>
                        )
                    },
                    {
                        title: t(tokens.nav.orderList),
                        role: [roles.ADMIN, roles.CUSTOMER, roles.CONTENT],
                        icon: (
                            <SvgIcon fontSize="small">
                                <ShoppingCart01Icon />
                            </SvgIcon>
                        ),
                        path: paths.dashboard.orders.index
                    },
                    {
                        title: tr("Email Templates"),
                        path: paths.dashboard.emailTemplates.index,
                        role: [roles.ADMIN, roles.CONTENT],
                        icon: (
                            <SvgIcon fontSize="small">
                                <Mail03Icon />
                            </SvgIcon>
                        )
                    },
                    {
                        title: tr("Partners"),
                        path: paths.dashboard.partners.index,
                        role: [roles.ADMIN, roles.CONTENT],
                        icon: (
                            <SvgIcon fontSize="small">
                                <Users03Icon />
                            </SvgIcon>
                        )
                    }
                ]
            },
            {
                subheader: t(tokens.nav.concepts),
                items: [
                    {
                        title: t(tokens.nav.productList),
                        path: paths.dashboard.products.index,
                        role: roles.ADMIN,
                        icon: (
                            <SvgIcon fontSize="small">
                                <ShoppingBag03Icon />
                            </SvgIcon>
                        ),
                        items: [
                            {
                                title: t(tokens.nav.list),
                                path: paths.dashboard.products.index
                            },
                            {
                                title: t(tokens.nav.create),
                                path: paths.dashboard.products.create
                            }
                        ]
                    },

                    {
                        title: t(tokens.nav.invoiceList),
                        path: paths.dashboard.invoices.index,
                        role: roles.ADMIN,
                        icon: (
                            <SvgIcon fontSize="small">
                                <ReceiptCheckIcon />
                            </SvgIcon>
                        ),
                        items: [
                            {
                                title: t(tokens.nav.list),
                                path: paths.dashboard.invoices.index
                            },
                            {
                                title: t(tokens.nav.details),
                                path: paths.dashboard.invoices.details
                            }
                        ]
                    },
                    {
                        title: t(tokens.nav.logistics),
                        path: paths.dashboard.logistics.index,
                        role: roles.ADMIN,
                        icon: (
                            <SvgIcon fontSize="small">
                                <Truck01Icon />
                            </SvgIcon>
                        ),
                        items: [
                            {
                                title: t(tokens.nav.dashboard),
                                path: paths.dashboard.logistics.index
                            },
                            {
                                title: t(tokens.nav.fleet),
                                path: paths.dashboard.logistics.fleet
                            }
                        ]
                    },
                    {
                        title: t(tokens.nav.academy),
                        path: paths.dashboard.academy.index,
                        role: roles.ADMIN,
                        icon: (
                            <SvgIcon fontSize="small">
                                <GraduationHat01Icon />
                            </SvgIcon>
                        ),
                        items: [
                            {
                                title: t(tokens.nav.dashboard),
                                path: paths.dashboard.academy.index
                            },
                            {
                                title: t(tokens.nav.course),
                                path: paths.dashboard.academy.courseDetails
                            }
                        ]
                    },
                    {
                        title: t(tokens.nav.socialMedia),
                        path: paths.dashboard.social.index,
                        role: roles.ADMIN,
                        icon: (
                            <SvgIcon fontSize="small">
                                <Share07Icon />
                            </SvgIcon>
                        ),
                        items: [
                            {
                                title: t(tokens.nav.publicprofile),
                                path: paths.dashboard.social.profile
                            },
                            {
                                title: t(tokens.nav.feed),
                                path: paths.dashboard.social.feed
                            }
                        ]
                    },
                    {
                        title: t(tokens.nav.blog),
                        path: paths.dashboard.blog.index,
                        role: roles.ADMIN,
                        icon: (
                            <SvgIcon fontSize="small">
                                <LayoutAlt02Icon />
                            </SvgIcon>
                        ),
                        items: [
                            {
                                title: t(tokens.nav.postList),
                                path: paths.dashboard.blog.index
                            },
                            {
                                title: t(tokens.nav.postDetails),
                                path: paths.dashboard.blog.postDetails
                            },
                            {
                                title: t(tokens.nav.postCreate),
                                path: paths.dashboard.blog.postCreate
                            }
                        ]
                    },
                    {
                        title: t(tokens.nav.fileManager),
                        path: paths.dashboard.fileManager,
                        role: roles.ADMIN,
                        icon: (
                            <SvgIcon fontSize="small">
                                <Upload04Icon />
                            </SvgIcon>
                        )
                    },
                    {
                        title: t(tokens.nav.kanban),
                        path: paths.dashboard.kanban,
                        role: roles.ADMIN,
                        icon: (
                            <SvgIcon fontSize="small">
                                <CheckDone01Icon />
                            </SvgIcon>
                        )
                    },
                    {
                        title: t(tokens.nav.mail),
                        path: paths.dashboard.mail,
                        role: roles.ADMIN,
                        icon: (
                            <SvgIcon fontSize="small">
                                <Mail03Icon />
                            </SvgIcon>
                        )
                    },
                    {
                        title: t(tokens.nav.chat),
                        path: paths.dashboard.chat,
                        role: roles.ADMIN,
                        icon: (
                            <SvgIcon fontSize="small">
                                <MessageChatSquareIcon />
                            </SvgIcon>
                        )
                    },
                    {
                        title: t(tokens.nav.serviceMessages),
                        path: paths.dashboard.serviceMessages,
                        role: [roles.ADMIN],
                        icon: (
                            <SvgIcon fontSize="small">
                                <Mail04Icon />
                            </SvgIcon>
                        )
                    },
                    {
                        title: t(tokens.nav.calendar),
                        path: paths.dashboard.calendar,
                        role: roles.ADMIN,
                        icon: (
                            <SvgIcon fontSize="small">
                                <CalendarIcon />
                            </SvgIcon>
                        )
                    }
                ]
            },
            {
                subheader: t(tokens.nav.pages),
                items: [
                    {
                        title: t(tokens.nav.auth),
                        role: roles.ADMIN,
                        icon: (
                            <SvgIcon fontSize="small">
                                <Lock01Icon />
                            </SvgIcon>
                        ),
                        items: [
                            {
                                title: t(tokens.nav.login),
                                items: [
                                    {
                                        title: tr("Classic"),
                                        path: paths.authDemo.login.classic
                                    },
                                    {
                                        title: tr("Modern"),
                                        path: paths.authDemo.login.modern
                                    }
                                ]
                            },
                            {
                                title: t(tokens.nav.register),
                                items: [
                                    {
                                        title: tr("Classic"),
                                        path: paths.authDemo.register.classic
                                    },
                                    {
                                        title: tr("Modern"),
                                        path: paths.authDemo.register.modern
                                    }
                                ]
                            },
                            {
                                title: t(tokens.nav.forgotPassword),
                                items: [
                                    {
                                        title: tr("Classic"),
                                        path: paths.authDemo.forgotPassword.classic
                                    },
                                    {
                                        title: tr("Modern"),
                                        path: paths.authDemo.forgotPassword.modern
                                    }
                                ]
                            },
                            {
                                title: t(tokens.nav.resetPassword),
                                items: [
                                    {
                                        title: tr("Classic"),
                                        path: paths.authDemo.resetPassword.classic
                                    },
                                    {
                                        title: tr("Modern"),
                                        path: paths.authDemo.resetPassword.modern
                                    }
                                ]
                            },
                            {
                                title: t(tokens.nav.verifyCode),
                                items: [
                                    {
                                        title: tr("Classic"),
                                        path: paths.authDemo.verifyCode.classic
                                    },
                                    {
                                        title: tr("Modern"),
                                        path: paths.authDemo.verifyCode.modern
                                    }
                                ]
                            }
                        ]
                    },
                    {
                        title: t(tokens.nav.pricing),
                        path: paths.pricing,
                        role: roles.ADMIN,
                        icon: (
                            <SvgIcon fontSize="small">
                                <CreditCard01Icon />
                            </SvgIcon>
                        )
                    },
                    {
                        title: t(tokens.nav.checkout),
                        path: paths.checkout,
                        role: roles.ADMIN,
                        icon: (
                            <SvgIcon fontSize="small">
                                <LogOut01Icon />
                            </SvgIcon>
                        )
                    },
                    {
                        title: t(tokens.nav.contact),
                        path: paths.contact,
                        role: roles.ADMIN,
                        icon: (
                            <SvgIcon fontSize="small">
                                <Mail04Icon />
                            </SvgIcon>
                        )
                    },
                    {
                        title: t(tokens.nav.error),
                        role: roles.ADMIN,
                        icon: (
                            <SvgIcon fontSize="small">
                                <XSquareIcon />
                            </SvgIcon>
                        ),
                        items: [
                            {
                                title: '401',
                                path: paths['401']
                            },
                            {
                                title: '404',
                                path: paths['404']
                            },
                            {
                                title: '500',
                                path: paths['500']
                            }
                        ]
                    }
                ]
            },
            {
                subheader: tr("Misc"),
                items: [
                    {
                        title: tr("Level 0"),
                        role: roles.ADMIN,
                        icon: (
                            <SvgIcon fontSize="small">
                                <AlignLeft02Icon />
                            </SvgIcon>
                        ),
                        items: [
                            {
                                title: tr("Level 1a"),
                                items: [
                                    {
                                        title: tr("Level 2a"),
                                        items: [
                                            {
                                                title: tr("Level 3a")
                                            },
                                            {
                                                title: tr("Level 3b"),
                                                disabled: true
                                            }
                                        ]
                                    },
                                    {
                                        title: tr("Level 2b")
                                    }
                                ]
                            },
                            {
                                title: tr("Level 1b")
                            }
                        ]
                    },
                    {
                        title: tr("Disabled"),
                        disabled: true,
                        role: roles.ADMIN,
                        icon: (
                            <SvgIcon fontSize="small">
                                <XSquareIcon />
                            </SvgIcon>
                        )
                    },
                    {
                        title: tr("Label"),
                        role: roles.ADMIN,
                        icon: (
                            <SvgIcon fontSize="small">
                                <File01Icon />
                            </SvgIcon>
                        ),
                        label: (
                            <Chip
                                color="primary"
                                label={tr("New")}
                                size="small"
                            />
                        )
                    },
                    {
                        title: tr("Blank"),
                        path: paths.dashboard.blank,
                        role: roles.ADMIN,
                        icon: (
                            <SvgIcon fontSize="small">
                                <File01Icon />
                            </SvgIcon>
                        )
                    },
                    {
                        title: tr("External Link"),
                        path: 'https://devias.io',
                        external: true,
                        role: roles.ADMIN,
                        icon: (
                            <SvgIcon fontSize="small">
                                <File01Icon />
                            </SvgIcon>
                        )
                    }
                ]
            }
        ];

        const filteredSections = sections
            .map((section) => {
                const filteredItems = filterNavItems(section.items, userRole);
                return filteredItems.length
                    ? { ...section, items: filteredItems }
                    : null;
            })
            .filter(Boolean);

        return filteredSections;
    }, [t, userRole]);
};